import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../api/client";
import { apiFetch } from "../../utils/apiFetch";
import QuestionMedia from "./QuestionMedia";

// Route: /inspections/:id/closeout
// Inspector-only panel. Reviews findings with status=evidence_uploaded,
// then accepts (→ closed) or declines (→ open) each one.
export default function CloseOutOpenPointsPanel() {
  const { inspectionId, token, user, inspection, answersByQid, setInspection, setEmailModal, requestSummary } = useOutletContext();

  const [items, setItems] = useState([]);       // evidence_uploaded findings
  const [openCount, setOpenCount] = useState(0); // still-open count for display
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [notesByFindingId, setNotesByFindingId] = useState({});
  const [err, setErr] = useState(null);
  const [interimLoading, setInterimLoading] = useState(false);
  const [completeOpenLoading, setCompleteOpenLoading] = useState(false);

  const isInspector =
    inspection?.inspector_user_id != null &&
    user?.id != null &&
    Number(inspection.inspector_user_id) === Number(user.id);

  // Build answer_id → question_id map from outlet context answersByQid
  const answerIdToQuestionId = useMemo(() => {
    const m = new Map();
    if (!answersByQid) return m;
    for (const [qid, row] of answersByQid.entries()) {
      const aid = row?.answer_id ?? row?.id ?? null;
      if (aid != null) m.set(Number(aid), Number(qid));
    }
    return m;
  }, [answersByQid]);

  async function load() {
    if (!inspectionId || !token) return;
    setLoading(true);
    setErr(null);
    try {
      const [evidenceData, openData] = await Promise.all([
        api(`/india/inspections/${inspectionId}/findings?status=evidence_uploaded`, { token }),
        api(`/india/inspections/${inspectionId}/findings?status=open`, { token }),
      ]);

      setItems(Array.isArray(evidenceData?.items) ? evidenceData.items : []);
      setOpenCount(Array.isArray(openData?.items) ? openData.items.length : 0);
    } catch (e) {
      setErr(e?.message || "Failed to load findings");
      setItems([]);
      setOpenCount(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionId, token]);

  async function setFindingStatus(findingId, status) {
    setErr(null);
    setActionId(findingId);
    try {
      const notes = notesByFindingId[findingId] || "";
      await api(`/india/inspections/${inspectionId}/findings/${findingId}`, {
        token,
        method: "PATCH",
        body: { status, verification_notes: notes },
      });
      await load();
    } catch (e) {
      setErr(e?.message || "Update failed");
    } finally {
      setActionId(null);
    }
  }

  async function handleComplete() {
    setErr(null);
    setCompleting(true);
    try {
      const result = await api(`/india/inspections/${inspectionId}/complete`, {
        token,
        method: "POST",
      });
      setInspection((prev) => ({ ...prev, status: result.status }));
    } catch (e) {
      setErr(e?.message || "Could not complete inspection");
    } finally {
      setCompleting(false);
    }
  }

  async function handleInterimReport() {
    setInterimLoading(true);
    try {
      const resp = await apiFetch(
        `${process.env.REACT_APP_API_URL}/india/inspections/${inspectionId}/interim-report`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error("Failed to generate interim report");
      const blob = await resp.blob();
      const serverFilename =
        resp.headers.get("Content-Disposition")?.match(/filename="?([^"]+)"?/)?.[1];
      const filename = serverFilename || `Interim-Report-${inspectionId}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (setEmailModal) setEmailModal("interim");
    } catch (e) {
      setErr(e?.message || "Failed to generate interim report");
    } finally {
      setInterimLoading(false);
    }
  }

  async function handleCompleteWithOpenPoints() {
    setCompleteOpenLoading(true);
    try {
      const resp = await apiFetch(
        `${process.env.REACT_APP_API_URL}/india/inspections/${inspectionId}/complete-with-open-points`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error("Failed to complete inspection");
      const blob = await resp.blob();
      const serverFilename =
        resp.headers.get("Content-Disposition")?.match(/filename="?([^"]+)"?/)?.[1];
      const filename = serverFilename || `Final-Report-${inspectionId}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (setEmailModal) setEmailModal("final");
    } catch (e) {
      setErr(e?.message || "Failed to complete inspection");
    } finally {
      setCompleteOpenLoading(false);
    }
  }

  // Non-inspector: show a clear message, don't render the panel
  if (!isInspector) {
    return (
      <div className="rounded-xl border bg-white p-4">
        <div className="text-sm font-semibold">Close out open points</div>
        <div className="text-sm text-gray-600 mt-1">
          This panel is for the assigned inspector only.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <div className="text-sm font-semibold">Close out open points</div>
          <div className="text-xs text-gray-600">
            Review submitted evidence. Accept to close, or decline to reopen.
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Awaiting review: <span className="font-semibold">{items.length}</span>
            {" · "}
            Still open: <span className="font-semibold">{openCount}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => load()}
          className="text-sm px-3 py-1 border rounded hover:bg-gray-50"
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {err && (
        <div className="text-sm text-red-700 mb-2 whitespace-pre-wrap">{String(err)}</div>
      )}

      {loading && <div className="text-sm text-gray-600">Loading…</div>}

      {!loading && items.length === 0 && openCount === 0 && inspection?.status !== "completed" && (
        <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-green-800">All findings closed</div>
            <div className="text-xs text-green-700 mt-0.5">
              The inspection is ready to be completed. This will unlock the Final PDF report.
            </div>
          </div>
          <button
            type="button"
            onClick={handleComplete}
            disabled={completing}
            className="shrink-0 px-4 py-2 rounded-lg bg-green-700 text-white text-sm font-medium hover:bg-green-800 disabled:opacity-50 transition"
          >
            {completing ? "Completing…" : "Complete inspection"}
          </button>
        </div>
      )}

      {isInspector && (
        <button
          type="button"
          onClick={() => requestSummary
            ? requestSummary("optional", handleInterimReport)
            : handleInterimReport()
          }
          disabled={interimLoading}
          className="shrink-0 px-4 py-2 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50 transition"
        >
          {interimLoading ? "Generating…" : "Generate Interim Report"}
        </button>
      )}

      {isInspector && openCount > 0 && (
        <button
          type="button"
          onClick={() => requestSummary
            ? requestSummary("optional", handleCompleteWithOpenPoints)
            : handleCompleteWithOpenPoints()
          }
          disabled={completeOpenLoading}
          className="shrink-0 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-50 transition"
        >
          {completeOpenLoading ? "Completing…" : "Complete with open points"}
        </button>
      )}

      {inspection?.status === "completed" && (
        <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4">
          <div className="text-sm font-semibold text-green-800">✓ Inspection completed</div>
          <div className="text-xs text-green-700 mt-0.5">
            The Final PDF report is now available.
          </div>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-sm text-gray-600">
          No findings awaiting review.
          {openCount > 0 && (
            <span className="ml-1 text-gray-400">
              ({openCount} still open — waiting for vessel to upload evidence)
            </span>
          )}
        </div>
      )}

      <div className="space-y-3">
        {items.map((f) => {
          const answerId = f.answer_id != null ? Number(f.answer_id) : null;
          const qid = answerId != null ? answerIdToQuestionId.get(answerId) : null;

          return (
            <div key={f.id} className="rounded-lg border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium">
                    {f.title || `Finding #${f.id}`}
                  </div>
                  {f.description && (
                    <div className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">
                      {f.description}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    Status: {f.status} · Severity: {f.severity || "n/a"} · ID: {f.id}
                    {qid != null && ` · Question #${qid}`}
                  </div>
                </div>

                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setFindingStatus(f.id, "closed")}
                    disabled={actionId === f.id}
                    className="text-sm px-3 py-2 border rounded hover:bg-gray-50 disabled:opacity-50"
                    title="Accept evidence and close finding"
                  >
                    {actionId === f.id ? "Working…" : "Accept"}
                  </button>

                  <button
                    type="button"
                    onClick={() => setFindingStatus(f.id, "open")}
                    disabled={actionId === f.id}
                    className="text-sm px-3 py-2 border rounded hover:bg-gray-50 disabled:opacity-50"
                    title="Decline evidence and reopen finding"
                  >
                    {actionId === f.id ? "Working…" : "Decline"}
                  </button>
                </div>
              </div>

              <div className="mt-3">
                {answerId ? (
                  <QuestionMedia
                    inspectionId={inspectionId}
                    answerId={answerId}
                    createdByUserId={user?.id}
                  />
                ) : (
                  <div className="text-xs text-gray-400">
                    No answer linked — media preview unavailable.
                  </div>
                )}
              </div>

              <div className="mt-3">
                <div className="text-xs font-semibold mb-1">Verification notes</div>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm"
                  rows={2}
                  placeholder="Reason for accept/decline, what was verified…"
                  value={notesByFindingId[f.id] || ""}
                  onChange={(e) =>
                    setNotesByFindingId((prev) => ({ ...prev, [f.id]: e.target.value }))
                  }
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
